import { z } from 'zod';
import { ATTENDANCE_REQUEST_STATUSES } from '../attendance/enums.js';
import { isoDate, paginated, paginationQuerySchema, queryList, uuid } from './common.js';
/** Work from home (FR-WFH-1…5, ADR-057, ADR-079). */
/** Most calendar days one WFH request may span (SRS §17). */
export const WFH_MAX_SPAN_DAYS = 31;
const spanDays = (from, to) => (Date.parse(to) - Date.parse(from)) / 86_400_000 + 1;
/**
 * `POST /wfh/requests`: a date (`toDate` omitted) or a range; only its working days are
 * requested. The caller's own request (`wfh.request`), decided by their reporting manager.
 */
export const wfhRequestCreateSchema = z
    .object({
    fromDate: isoDate,
    toDate: isoDate.optional(),
    reason: z.string().trim().min(3).max(500),
})
    .refine((v) => v.toDate === undefined || v.fromDate <= v.toDate, {
    path: ['toDate'],
    message: 'Must not be before the first day',
})
    .refine((v) => v.toDate === undefined || spanDays(v.fromDate, v.toDate) <= WFH_MAX_SPAN_DAYS, {
    path: ['toDate'],
    message: `At most ${String(WFH_MAX_SPAN_DAYS)} days per request`,
});
const person = z.object({ id: uuid, empCode: z.int(), fullName: z.string() });
/**
 * One WFH request: its id is the approval request's (the days follow it). `dates` are the
 * working days requested; `canCancel` says whether the caller may withdraw / cancel it now
 * (their own, pending, or approved before its first day — FR-WFH-3).
 */
export const wfhRequestItemSchema = z.object({
    id: uuid,
    employee: person,
    fromDate: isoDate,
    toDate: isoDate,
    dates: z.array(isoDate),
    reason: z.string().nullable(),
    status: z.enum(ATTENDANCE_REQUEST_STATUSES),
    requestedAt: z.string(),
    canCancel: z.boolean(),
});
export const WFH_SCOPES = ['me', 'team', 'all'];
/** `GET /wfh`: newest first; `from` / `to` keep requests with a day in that range. */
export const wfhListQuerySchema = paginationQuerySchema.extend({
    scope: z.enum(WFH_SCOPES).default('me'),
    status: queryList(z.enum(ATTENDANCE_REQUEST_STATUSES)).optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
});
export const wfhListResponseSchema = paginated(wfhRequestItemSchema);
/** `GET /wfh/calendar?scope=team|all&from=&to=` (at most 62 days). */
export const wfhCalendarQuerySchema = z
    .object({ scope: z.enum(['team', 'all']).default('team'), from: isoDate, to: isoDate })
    .refine((v) => v.from <= v.to, { path: ['to'], message: 'Must not be before `from`' })
    .refine((v) => spanDays(v.from, v.to) <= 62, { path: ['to'], message: 'At most 62 days' });
/** Pending and approved WFH days in the range; no reasons. */
export const wfhCalendarResponseSchema = z.object({
    from: isoDate,
    to: isoDate,
    items: z.array(z.object({
        requestId: uuid,
        employee: person,
        date: isoDate,
        status: z.enum(['PENDING', 'APPROVED']),
    })),
});
//# sourceMappingURL=wfh.js.map